import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Switch } from "@/components/ui/switch";
import { Bell, Loader2 } from "lucide-react";
import { toast } from "sonner";

type Prefs = {
  new_applicants: boolean;
  shift_reminders: boolean;
  payouts: boolean;
  urgent_broadcasts: boolean;
  reviews: boolean;
};

const DEFAULTS: Prefs = {
  new_applicants: true,
  shift_reminders: true,
  payouts: true,
  urgent_broadcasts: true,
  reviews: true,
};

const LABELS: Record<keyof Prefs, { title: string; sub: string }> = {
  new_applicants: { title: "New applicants", sub: "When someone applies to your gig" },
  shift_reminders: { title: "Shift reminders", sub: "30 minutes before a shift starts" },
  payouts: { title: "Payouts", sub: "When escrow is released to your wallet" },
  urgent_broadcasts: { title: "Urgent gigs nearby", sub: "Now / urgent jobs matching your skills" },
  reviews: { title: "Reviews", sub: "When someone reviews you" },
};

export const NotificationPrefs = () => {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState<Prefs>(DEFAULTS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    supabase
      .from("notification_prefs" as any)
      .select("*")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) setPrefs({ ...DEFAULTS, ...(data as any) });
        setLoading(false);
      });
  }, [user]);

  const toggle = async (k: keyof Prefs, v: boolean) => {
    if (!user) return;
    const next = { ...prefs, [k]: v };
    setPrefs(next);
    const { error } = await supabase
      .from("notification_prefs" as any)
      .upsert({ user_id: user.id, ...next, updated_at: new Date().toISOString() });
    if (error) {
      toast.error(error.message);
      setPrefs(prefs);
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5 space-y-4">
      <div className="flex items-center gap-2">
        <Bell className="h-4 w-4 text-primary" />
        <h3 className="font-display font-bold">Notifications</h3>
      </div>
      {loading ? (
        <div className="grid place-items-center py-4 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      ) : (
        <div className="space-y-2">
          {(Object.keys(LABELS) as (keyof Prefs)[]).map((k) => (
            <div
              key={k}
              className="flex items-start justify-between gap-3 rounded-xl bg-secondary/40 px-3 py-2"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{LABELS[k].title}</p>
                <p className="text-xs text-muted-foreground">{LABELS[k].sub}</p>
              </div>
              <Switch checked={prefs[k]} onCheckedChange={(v) => toggle(k, v)} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
